import { CoreTelemetryClient, Metadata } from '@pp/telemetry';

import { LoggerAdapter } from './LoggerAdapter';
import type { IDurationOptions, IEventOptions, IFailureOptions, ILogPayloadBase } from './types';

export class TelemetryLogger extends LoggerAdapter {
  constructor(private telemetryClient: CoreTelemetryClient) {
    super();
  }

  gatherCustomMeta(payload?: Partial<ILogPayloadBase>): Partial<Metadata> | undefined {
    if (!payload) {
      return;
    }
    const { casino, tableId } = payload;
    if (!casino && !tableId) {
      return;
    }
    return {
      tableId: tableId,
      casinoId: casino,
    };
  }

  public logEvent(
    metricOrLoggerName: string,
    optionsOrMetric?: string | IEventOptions,
    _timestamp?: number,
    count?: number,
    key?: string,
    instant?: boolean,
    bandwidth?: number
  ): void {
    let data: {
      name: string;
      count: number;
      payload: [additional: string, ...string[]];
      numberPayload: [number];
    };
    let isInstant: boolean;
    let customMeta: Partial<Metadata> | undefined;

    if (typeof optionsOrMetric !== 'string') {
      const options = optionsOrMetric;
      isInstant = options?.instant ?? false;
      data = {
        count: options?.count ?? 1,
        name: metricOrLoggerName,
        payload: [options?.additional ?? ''],
        numberPayload: [options?.bandwidth ?? 0],
      };
      customMeta = this.gatherCustomMeta(options);
    } else {
      isInstant = instant ?? false;
      data = {
        count: count ?? 1,
        name: optionsOrMetric,
        payload: [key ?? ''],
        numberPayload: [bandwidth ?? 0],
      };
    }

    this.telemetryClient.reportEvent(data, {
      instant: isInstant,
      customMeta,
    });
  }

  public logFailure(
    typeOrLoggerName: string,
    optionsOrType?: string | IFailureOptions,
    place?: string,
    _timestamp?: number,
    count?: number,
    key?: string,
    instant?: boolean
  ): void {
    let data: {
      name: string;
      count: number;
      payload: [place: string, additional: string];
      numberPayload: [bandwidth: number];
    };
    let isInstant: boolean;
    let customMeta: Partial<Metadata> | undefined;

    if (typeof optionsOrType !== 'string') {
      const options = optionsOrType;
      isInstant = options?.instant ?? false;
      data = {
        name: typeOrLoggerName,
        count: options?.count ?? 1,
        payload: [options?.place ?? '', options?.additional ?? ''],
        numberPayload: [options?.bandwidth ?? 0],
      };
      customMeta = this.gatherCustomMeta(options);
    } else {
      isInstant = instant ?? false;
      data = {
        count: count ?? 1,
        name: optionsOrType,
        payload: [place ?? '', key ?? ''],
        numberPayload: [0],
      };
    }

    this.telemetryClient.reportFailure(data, {
      instant: isInstant,
      customMeta,
    });
  }

  public logDuration(
    metricOrLoggerName: string,
    optionsOrMetric: string | IDurationOptions,
    _timestamp?: number,
    duration?: number,
    key?: string,
    instant?: boolean,
    bandwidth?: number
  ): void {
    let data: {
      name: string;
      count: number;
      payload: [additional: string];
      numberPayload: [duration: number, bandwidth: number, ...number[]];
    };
    let isInstant: boolean;
    let customMeta: Partial<Metadata> | undefined;

    if (typeof optionsOrMetric === 'string') {
      isInstant = instant ?? false;
      data = {
        name: optionsOrMetric,
        count: 1,
        payload: [key ?? ''],
        numberPayload: [duration ?? 0, bandwidth ?? 0],
      };
    } else {
      isInstant = optionsOrMetric.instant ?? false;
      data = {
        name: metricOrLoggerName,
        count: 1,
        payload: [optionsOrMetric.additional ?? ''],
        numberPayload: [optionsOrMetric.duration ?? 0, optionsOrMetric.bandwidth ?? 0],
      };
      customMeta = this.gatherCustomMeta(optionsOrMetric);
    }

    this.telemetryClient.reportDuration(data, {
      instant: isInstant,
      customMeta,
    });
  }

  public logSearchQuery(query: string, vendor: string, result: string): void {
    this.telemetryClient.reportSearch(
      {
        name: 'searchQuery',
        payload: [query, result],
      },
      {
        customMeta: {
          vendor: vendor,
        },
      }
    );
  }

  public logFailedTranslation(translationKey: string, language?: string): void {
    let customMeta: Partial<Metadata> | undefined;
    if (language) {
      customMeta = {
        language,
      };
    }

    this.telemetryClient.reportTranslation(
      {
        name: 'notFound',
        payload: [translationKey],
      },
      {
        customMeta,
        once: () => translationKey,
      }
    );
  }

  public logException(error: Error): void {
    const payload: string[] = [];
    payload.push(error?.message || 'Unknown Error');
    if (error?.stack) {
      payload.push(error.stack);
    }

    this.telemetryClient.reportText(payload.join('\n'), {
      instant: true,
    });
  }
}
